import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ToastrService} from 'ngx-toastr';
import {ProgramService} from '../program.service';
import {IdAndNameOption, ProductService} from '../../product/product.service';
import {ReferentialService} from 'src/app/common/referential/referential.service';
import {UserDTO} from 'src/app/common/user.service';
import {branches, partnerCompanies} from 'src/app/common/util/const';
import { Editor, Toolbar, NgxEditorModule } from 'ngx-editor';
import {addPasteSanitizer} from 'src/app/common/util/plugins';
import {SharedEventsService} from 'src/app/common/shared-events-service';
import {ConfirmPopupComponent} from '../../common/confirm-popup/confirm-popup.component';
import {Product} from 'src/app/product/product.service';
import {EnablerItemUtil} from "../../common/util/enabler-item-util";
import {
  GLOBAL_PROGRESS_MODE_HELP,
  MONO_PROGRAM_WORKSTREAM_PROGRESS_MODE_HELP, MULTI_PROGRAM_ENABLER_DATA_PLATFORM_WORKSTREAM_PROGRESS_MODE_HELP, ProgressModeHelpMap
} from "../../common/util/progress-mode-help";
import {ProgressMode, ProgressUtil} from "../../common/util/progress-util";
import { PopupHeaderComponent } from '../../common/popup-header/popup-header.component';
import { MatTooltip } from '@angular/material/tooltip';
import { NgSelectComponent } from '@ng-select/ng-select';
import { NgClass, NgStyle } from '@angular/common';
import { UserAutocompleteComponent } from '../../common/user-autocomplete/user-autocomplete.component';

declare const bootstrap: any;

@Component({
    selector: 'app-add-program',
    templateUrl: './add-program.component.html',
    styleUrls: ['./add-program.component.scss'],
    imports: [PopupHeaderComponent, FormsModule, ReactiveFormsModule, MatTooltip, NgSelectComponent, NgxEditorModule, NgClass, UserAutocompleteComponent, NgStyle]
})
export class AddProgramComponent implements OnInit, OnDestroy {
  programType: 'single' | 'multi' = 'multi';
  gates: Array<{ id: string; name: string; weight: number, hasDeploymentGroups: boolean }> = [{
    id: null,
    name: '',
    weight: 100,
    hasDeploymentGroups: false
  }];
  addProgramForm: FormGroup;
  branches = branches;
  entityOwnerOptions: IdAndNameOption[] = [];
  partnerCompanies = partnerCompanies;
  familyOptions = ['IPM', 'PDO'];
  allProgramNames: string[] = [];
  allProgramTrigrams: string[] = [];
  availableProductsForWorkstream: Product[] = [];
  availableEnablerItems: any[] = []; // Mix of enabler projects and products with type
  availableProducts: any[] = [];

  public editor: Editor;
  public toolbar: Toolbar = [
    ["bold", "italic"],
    ["underline", "strike"],
    ["ordered_list", "bullet_list"],
    [{heading: ["h1", "h2", "h3", "h4", "h5", "h6"]}],
    ["text_color"],
    ["align_left", "align_center", "align_right", "align_justify"],
  ];
  ambition: string = "";

  CONTACTS_LIST = [
    "Executive Sponsor",
    "Operational Sponsor",
    "Program Director",
    "SME",
    "PVM",
    "Asset Lead / Domain Lead",
    "Data et technology Lead",
    "Partner contacts"
  ];

  singleContactRoles = ["PVM", "Data et technology Lead", "Program Director"];
  noFteContactRoles = ["Executive Sponsor", "Operational Sponsor", "Partner contacts"];

  openedWorkstreams: { [key: number]: boolean } = {};
  readonly globalProgressModeHelp = GLOBAL_PROGRESS_MODE_HELP;
  workstreamProgressModeHelp: ProgressModeHelpMap<ProgressMode>;

  constructor(
    private readonly fb: FormBuilder,
    public dialogRef: MatDialogRef<AddProgramComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly programService: ProgramService,
    private readonly productService: ProductService,
    private readonly toastr: ToastrService,
    private readonly sharedEventsService: SharedEventsService,
    private readonly referentialService: ReferentialService,
    private readonly dialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.initializeProgramType();
    this.initializeEditor();
    this.loadInitialData();
    this.initializeForm();

    if (this.data?.program) {
      this.patchFormWithProgramData();
      this.handleWorkstreamsForEdit();
      this.handleGatesForEdit();
      this.handleContactsForEdit();
    } else {
      if (this.programType == 'single') this.gates.push({
        id: null,
        name: 'Full Value',
        weight: 0,
        hasDeploymentGroups: false
      }); // Default Full Value gate
      this.addDefaultContactsAndWorkstream();
    }

    if (this.data?.goToWorkstream) {
      bootstrap.Tab.getOrCreateInstance(document.querySelector("#add-program-tabs button[data-bs-target='#workstreams-tab']")).show();
      for (let index in this.openedWorkstreams) this.openedWorkstreams[index] = false;

      this.openedWorkstreams[this.workstreams.controls.findIndex(control => control.value.name == this.data.goToWorkstream)] = true;
    }

    if (this.data?.goToDeploymentGates) {
      setTimeout(() => {
        bootstrap.Tab.getOrCreateInstance(document.querySelector("#add-program-tabs button[data-bs-target='#gates-tab']")).show();
      });
    }
  }

  private initializeProgramType(): void {
    if (this.data?.programType) {
      this.programType = this.data.programType;
    }
    if (this.programType === 'single') {
      this.productService.getAllProducts().subscribe(products => this.availableProductsForWorkstream = products);
      this.workstreamProgressModeHelp = MONO_PROGRAM_WORKSTREAM_PROGRESS_MODE_HELP;
    }
    else this.workstreamProgressModeHelp = MULTI_PROGRAM_ENABLER_DATA_PLATFORM_WORKSTREAM_PROGRESS_MODE_HELP;
  }

  private initializeEditor(): void {
    this.editor = new Editor();
    addPasteSanitizer(this.editor);
  }

  private loadInitialData(): void {
    this.programService.getAllProgramIdAndNamesAndFamily().subscribe(idAndNames => {
      this.allProgramNames = idAndNames.map(n => n.name.trim().toLowerCase());
    });
    this.referentialService.getReferences("entity-owners").subscribe(entityOwners => this.entityOwnerOptions = entityOwners);
    this.programService.getAllProgramTrigrams().subscribe(trigrams => {
      this.allProgramTrigrams = trigrams.map(t => t.trim().toLowerCase());
    });
    this.programService.getProductsForProgram(this.data?.program?.id).subscribe(products => {
      this.availableProductsForWorkstream = products;
      this.updateAvailableProducts();
    });
    this.programService.getEnablerProjectsForProgram().subscribe(items => {
      this.availableEnablerItems = items || [];
      this.availableEnablerItems.forEach(item => {
        item.idFormatted = item.type == "PROJECT" ? "PROJECT-" + item.id : "PRODUCT-" + item.id;
      });
      this.updateAvailableProducts();
    });
  }

  // Mono-product programs are attached to a single product (enabler projects and enabler products are listed as products too)
  private updateAvailableProducts(): void {
    this.availableProducts = [
      ...this.availableProductsForWorkstream.map(p => ({name: p.name, idFormatted: "PRODUCT-" + p.id})),
      ...this.availableEnablerItems
    ];
  }

  private initializeForm(): void {
    this.addProgramForm = ProgressUtil.withManualProgressValidation(this.fb.group({
      id: [null],
      name: ['', [Validators.required, this.programNameValidator.bind(this)]],
      trigram: ['', [Validators.required, this.programTrigramValidator.bind(this), Validators.pattern(/^[a-zA-Z]{3}$/)]],
      ambition: [''],
      branches: [[]],
      entityOwnerId: [null],
      partnerCompany: [''],
      family: [''],
      faitMarquantName: [''],
      progress: [0],
      progressMode: ['auto'],
      contacts: this.fb.array([]),
      workstreams: this.fb.array([]),
      selectedProduct: this.programType === 'single' ? [null, Validators.required] : undefined
    }));
  }

  private patchFormWithProgramData(): void {
    this.addProgramForm.patchValue(this.data.program);
    this.ambition = this.data.program.ambition || '';
    const progressMode = this.data.program.progressMode;
    this.addProgramForm.get('progressMode').setValue(progressMode);
      this.addProgramForm.get('progress').setValue(progressMode === 'manual' ? (this.data.program.progress ?? 0) : 0);
    if (this.programType === 'single' && this.data.program) {
      const {product, enablerProjectId} = this.data.program;
      this.addProgramForm.get('selectedProduct').setValue(product ? "PRODUCT-" + product.id : "PROJECT-" + enablerProjectId);
    }
  }

  private handleWorkstreamsForEdit(): void {
    if (this.data.program.workstreams?.length > 0) {
      for (const [index, workstream] of this.data.program.workstreams.entries()) {
        this.addWorkstream(workstream);
        this.openedWorkstreams[index] = false;
      }
    } else {
      this.addWorkstream();
      this.openedWorkstreams[0] = true;
    }
  }

  private handleGatesForEdit(): void {
    if (this.programType === 'single' && Array.isArray(this.data.program.deploymentGates)) {
      this.gates = this.data.program.deploymentGates.map(g => ({
        id: g.id,
        name: g.name,
        weight: g.weight,
        hasDeploymentGroups: g.hasDeploymentGroups
      }));
    }
  }

  private handleContactsForEdit(): void {
    this.contacts.clear();
    const contactsByRole = (this.data.program.contacts || []).reduce((acc, contact) => {
      acc[contact.role] = acc[contact.role] || [];
      acc[contact.role].push(contact);
      return acc;
    }, {});

    for (const role of this.CONTACTS_LIST) {
      const contactsForRole = contactsByRole[role] || [];
      if (this.noFteContactRoles.includes(role)) {
        const users = contactsForRole.map(c => c.user).filter(Boolean);
        this.onMultiUsersSelected(users, role);
      } else if (contactsForRole.length > 0) {
        for (const c of contactsForRole) {
          this.addContact(c.role, c.user, c.fte);
        }
      } else {
        this.addContact(role);
      }
    }
  }

  fillExistingValues() {
    this.addProgramForm.patchValue(this.data.program);
    this.ambition = this.data.program.ambition || '';


    if (this.data.program.workstreams?.length > 0) {
      for (const [index, workstream] of this.data.program.workstreams.entries()) {
        this.addWorkstream(workstream);
        this.openedWorkstreams[index] = false;
      }
    } else {
      this.addWorkstream();
      this.openedWorkstreams[0] = true;
    }

    this.contacts.clear();

    const contactsByRole = (this.data.program.contacts || []).reduce((acc, contact) => {
      acc[contact.role] = acc[contact.role] || [];
      acc[contact.role].push(contact);
      return acc;
    }, {});

    for (const role of this.CONTACTS_LIST) {
      const contactsForRole = contactsByRole[role] || [];
      if (this.noFteContactRoles.includes(role)) {
        const users = contactsForRole.map(c => c.user).filter(Boolean);
        this.onMultiUsersSelected(users, role);
      } else if (contactsForRole.length > 0) {
        for (const c of contactsForRole) {
          this.addContact(c.role, c.user, c.fte);
        }
      } else {
        this.addContact(role);
      }
    }
  }

  private addDefaultContactsAndWorkstream(): void {
    for (const role of this.CONTACTS_LIST) {
      this.addContact(role);
    }
    this.addWorkstream();
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  toggleWorkstream(index: number): void {
    this.openedWorkstreams[index] = !this.openedWorkstreams[index];
  }

  isWorkstreamOpen(index: number): boolean {
    return !!this.openedWorkstreams[index];
  }

  programNameValidator(control: FormControl): { [s: string]: boolean } {
    const name = (control.value || '').trim().toLowerCase();
    const previousName = this.data?.program?.name?.trim().toLowerCase();
    // Always check if name exists, but allow if it's the same as the previous value for this program
    if (this.allProgramNames.includes(name) && name !== previousName) {
      return {programNameExists: true};
    }
    return null;
  }

  programTrigramValidator(control: FormControl): { [s: string]: boolean } {
    const trigram = (control.value || '').trim().toLowerCase();
    const currentId = this.addProgramForm ? this.addProgramForm.get('id').value : null;
    const previousTrigram = this.data?.program?.trigram?.trim().toLowerCase();

    // Always check if trigram exists, but allow if it's the same as the previous value for this program
    if (
      this.allProgramTrigrams.includes(trigram) &&
      (!currentId || trigram !== previousTrigram)
    ) {
      return {programTrigramExists: true};
    }
    return null;
  }

  getDialogTitle(): string {
    return this.data?.program?.id ? 'Edit Program' : 'Add Program';
  }

  get contacts(): FormArray {
    return this.addProgramForm.get('contacts') as FormArray;
  }

  get workstreams(): FormArray {
    return this.addProgramForm.get('workstreams') as FormArray;
  }

  getContactsByRole(role: string): AbstractControl[] {
    return this.contacts.controls.filter(control => control.get('role').value === role);
  }

  getUsersForRole(role: string): UserDTO[] {
    return this.getContactsByRole(role)
      .map(control => control.get('user').value)
      .filter(Boolean);
  }

  addContact(role: string, user: UserDTO = null, fte: number = null): void {
    const fteValidators = [Validators.min(0), Validators.max(100)];
    if (user && !this.noFteContactRoles.includes(role)) {
      fteValidators.push(Validators.required);
    }

    const contactForm = this.fb.group({
      user: [user, role === 'Program Director' ? Validators.required : null],
      role: [role, Validators.required],
      fte: [fte, fteValidators]
    });
    this.contacts.push(contactForm);
  }

  removeContact(index: number): void {
    this.contacts.removeAt(index);
  }

  createWorkstream(workstream: any = {}): FormGroup {
    return ProgressUtil.withManualProgressValidation(this.fb.group({
      id: [workstream.id || null],
      name: [workstream.name || '', Validators.required],
      description: [workstream.description || '', Validators.required],
      manager: [workstream.manager || null, Validators.required],
      managerFte: [workstream.managerFte || null, Validators.required],
      productIds: [workstream.productIds || []],
      enablerItemIds: [EnablerItemUtil.mergeEnablerIds(workstream.enablerProjectIds, workstream.enablerProductIds)],
      hasRoadmapGroups: [workstream.hasRoadmapGroups || false],
      progress: [workstream?.progressMode === 'manual' ? (workstream.progress ?? 0) : 0],
      progressMode: [workstream?.progressMode ?? 'auto'],
      groupWeights: ProgressUtil.buildGroupWeightsArray(workstream, this.fb),
      weight: [workstream.weight ?? null],
    }));
  }

  addWorkstream(workstream: any = {}): void {
    const newIndex = this.workstreams.length;
    if (this.programType === 'single') {
      this.workstreams.push(ProgressUtil.withManualProgressValidation(this.fb.group({
        id: [workstream.id || null],
        name: [workstream.name || '', Validators.required],
        description: [workstream.description || '', Validators.required],
        manager: [workstream.manager || null, Validators.required],
        managerFte: [workstream.managerFte || null, Validators.required],
        enablerItemIds: [EnablerItemUtil.mergeEnablerIds(workstream.enablerProjectIds, workstream.enablerProductIds)],
           progress: [workstream.progress ?? 0],
        progressMode: [workstream?.progressMode ?? 'auto'],
        groupWeights: ProgressUtil.buildGroupWeightsArray(workstream, this.fb),
        weight: [workstream.weight ?? null],
      })));
    } else {
      this.workstreams.push(this.createWorkstream(workstream));
    }
    this.openedWorkstreams[newIndex] = true;
  }

  setProgressMode(i: number, mode: ProgressMode): void {
    return ProgressUtil.setWorkstreamProgressMode(this.workstreams, i, mode);
  }

  setParentProgressMode(mode: ProgressMode): void {
    ProgressUtil.setProgressMode(this.addProgramForm, mode);
  }

  getTotalWorkstreamWeight(): number {
    return ProgressUtil.getTotalWorkstreamWeight(this.workstreams);
  }

  hasInvalidParentWorkstreamWeights(): boolean {
    return ProgressUtil.hasInvalidParentWorkstreamWeights(this.addProgramForm, this.workstreams);
  }

  getTotalGroupWeight(i: number): number {
    return ProgressUtil.getTotalGroupWeight(this.workstreams, i);
  }

  getGroupWeightsArray(i: number) {
    return ProgressUtil.getGroupWeightsArray(this.workstreams, i);
  }

  hasInvalidWorkstreamWeights(): boolean {
    return ProgressUtil.hasInvalidWorkstreamWeights(this.workstreams);
  }

  removeWorkstream(index: number): void {
    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      width: "30vw",
      data: {
        icon: "delete",
        title: "Delete Workstream",
        text: "Are you sure you want to delete this workstream?",
      },
      position: {
        top: "20px",
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.workstreams.removeAt(index);
        delete this.openedWorkstreams[index];
        // Re-index the openedWorkstreams object
        const newOpenedWorkstreams = {};
        for (let i = 0; i < this.workstreams.length; i++) {
          if (this.openedWorkstreams[i + 1] === undefined) {
            newOpenedWorkstreams[i] = this.openedWorkstreams[i] || true;
          } else {
            newOpenedWorkstreams[i] = this.openedWorkstreams[i + 1];
          }
        }
        this.openedWorkstreams = newOpenedWorkstreams;
      }
    });
  }

  onSingleUserSelected(user: UserDTO, control: FormGroup): void {
    control.get('user').setValue(user);

    const fteControl = control.get('fte');
    if (user) {
      fteControl.setValue(100);
      fteControl.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
    } else {
      fteControl.clearValidators();
      fteControl.setValidators([Validators.min(0), Validators.max(100)]);
    }
    fteControl.updateValueAndValidity();
  }

  onMultiUsersSelected(users: UserDTO[], role: string): void {
    const indicesToRemove = this.contacts.controls
      .map((control, index) => (control.value.role === role ? index : -1))
      .filter(index => index !== -1)
      .reverse();

    for (const index of indicesToRemove) {
      this.contacts.removeAt(index);
    }

    if (users?.length > 0) {
      for (const user of users) {
        this.addContact(role, user, null);
      }
    } else {
      this.addContact(role);
    }
  }


  onWorkstreamManagerSelected(users: UserDTO[], i: number): void {
    const user = users[0] ?? null;
    const workstreamControl = this.workstreams.at(i) as FormGroup;
    workstreamControl.get('manager').setValue(user);
    const fteControl = workstreamControl.get('managerFte');
    if (user) {
      fteControl.setValue(100);
      fteControl.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
    } else {
      fteControl.clearValidators();
      fteControl.setValidators([Validators.min(0), Validators.max(100)]);
    }
    fteControl.updateValueAndValidity();
  }

  onWorkstreamProductsChanged(productIds: number[], i: number): void {
    // For each product newly added to this workstream, remove it from all other workstreams
    for (let wsIdx = 0; wsIdx < this.workstreams.length; wsIdx++) {
      if (wsIdx === i) continue;
      const wsControl = this.workstreams.at(wsIdx);
      let wsProductIds = wsControl.get('productIds').value as number[];
      const filtered = wsProductIds.filter(pid => !productIds.includes(pid));
      if (filtered.length !== wsProductIds.length) {
        wsControl.get('productIds').setValue(filtered);
      }
    }
  }

  onWorkstreamItemsChanged(enablerItemIds: string[], i: number): void {
    // For each enabler item newly added to this workstream, remove it from all other workstreams
    for (let wsIdx = 0; wsIdx < this.workstreams.length; wsIdx++) {
      if (wsIdx === i) continue;
      const wsControl = this.workstreams.at(wsIdx);
      let wsEnablerItemIds = wsControl.get('enablerItemIds').value as string[];
      const filtered = wsEnablerItemIds.filter(eid => !enablerItemIds.includes(eid));
      if (filtered.length !== wsEnablerItemIds.length) {
        wsControl.get('enablerItemIds').setValue(filtered);
      }
    }
  }

  onSubmit(): void {
    if (this.addProgramForm.invalid || (this.programType === 'single' && this.getTotalGateWeight() !== 100)) {
      this.addProgramForm.markAllAsTouched();
      for (const control of this.contacts.controls as FormGroup[]) {
        control.markAllAsTouched();
      }
      this.toastr.error('Please fill all required fields.' + (this.programType === 'single' ? ' Total gate weight must be 100%.' : ''));
      return;
    }

    const formValues = this.addProgramForm.getRawValue();
    formValues.ambition = this.ambition;
    formValues.contacts = formValues.contacts.filter(c => c.user || c.fte);

    if (this.programType === 'single') {
      formValues.deploymentGates = this.gates;
      const [productType, productId] = formValues.selectedProduct.split('-');
      formValues.product = productType === 'PRODUCT' ? {id: Number(productId)} : null;
      formValues.enablerProjectId = productType === 'PROJECT' ? Number(productId) : null;
      delete formValues.selectedProduct;
    }
    if (formValues.workstreams && this.programType === 'multi') {
      for (const ws of formValues.workstreams) {
        // Separate enabler items
        if (ws.enablerItemIds) {
          const {
            projectIds,
            productIds
          } = EnablerItemUtil.separateEnablerItems(ws.enablerItemIds, this.availableEnablerItems);
          ws.enablerProjectIds = projectIds;
          ws.enablerProductIds = productIds;
          delete ws.enablerItemIds;
        }
        if (ws.productIds && ws.productIds.length > 0 && typeof ws.productIds[0] === 'object') {
          ws.productIds = ws.productIds.map(p => p.id);
        }
      }
    }
    if (formValues.workstreams) {
      for (const ws of formValues.workstreams) {
        ProgressUtil.applyProgressMode(ws);
      }
    }
    ProgressUtil.applyParentProgressMode(formValues);
    this.programService.saveProgram(formValues).subscribe({
      next: (program) => {
        this.toastr.success('Program saved successfully!');
        this.sharedEventsService.notifyReloadHeader();
        this.dialogRef.close(program.id);
      },
      error: (error) => {
        this.toastr.error(error.error);
      },
    });
  }

  addGate(): void {
    // Insert the new gate before the full value gate
    const newGate = {id: null, name: '', weight: 0, hasDeploymentGroups: false};
    if (this.gates.length > 0) {
      this.gates.splice(-1, 0, newGate);
    } else {
      this.gates.push(newGate);
    }
  }

  removeGate(index: number): void {
    if (this.gates.length > 1) {
      this.gates.splice(index, 1);
    }
  }

  onGateWeightChange(): void {
    const fullValueGate = this.gates.find(gate => gate.name === "Full Value");
    if (fullValueGate) {
      fullValueGate.weight = Math.max(0, 100 - this.getTotalGateWeight() + fullValueGate.weight);
    }
  }

  getTotalGateWeight(): number {
    return this.gates.reduce((sum, gate) => sum + (Number(gate.weight) || 0), 0);
  }


  onCancel(): void {
    this.dialogRef.close();
  }

  isTabDetailsInvalid(): boolean {
    if (!this.addProgramForm) {
      return false;
    }
    const fields = ["name", "trigram"];
    if (this.programType === 'single') {
      fields.push("product");
    }
    for (const field of fields) {
      const control = this.addProgramForm.get(field);
      if (control?.invalid) {
        return true;
      }
    }
    return false;
  }

  isTabContactsInvalid(): boolean {
    if (!this.addProgramForm) {
      return false;
    }
    const contactsControl = this.addProgramForm.get('contacts');
    return contactsControl ? contactsControl.invalid : false;
  }

  isTabWorkstreamsInvalid(): boolean {
    if (!this.addProgramForm) {
      return false;
    }
    const workstreamsControl = this.addProgramForm.get('workstreams');
    return workstreamsControl ? workstreamsControl.invalid : false;
  }

  isTabGatesInvalid(): boolean {
    if (this.programType !== 'single') {
      return false;
    }
    // Gates must exist, all must have a name and weight, and total weight must be 100
    if (!this.gates || this.gates.length === 0) {
      return true;
    }
    for (const gate of this.gates) {
      if (!gate.name || gate.name.trim() === '' || gate.weight === null || gate.weight === undefined || Number.isNaN(Number(gate.weight))) {
        return true;
      }
    }
    return this.getTotalGateWeight() !== 100;
  }

  hasInvalidFullValueGate(): boolean {
    if (this.programType !== 'single') return false;
    const fullValueGates = this.gates.filter(g => g.name?.trim() === 'Full Value');
    return fullValueGates.length > 1;
  }
}
